<script lang="ts" setup>
import { computed, inject, nextTick, onMounted, onUnmounted, PropType, Ref, ref, watch } from 'vue';
import {
  TirPmIconView,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
  TirPmPagination,
  TirPmScrollbar,
} from 'tir-components';
import { debounce } from 'tir-pm-debounce';
import { ProjectModel } from '@/models';
import { TasksOrderPagesAdapterService, UpdateTaskAdapterService } from '../../adapter-service';
import {
  AddOrRemoveTaskEventDataModel,
  GetProjectTasksRequestModel,
  OrderTaskModel,
  PaginationRequestModel,
  PaginationResponseModel,
  TaskColumnModel,
  TaskFilterModel,
  TaskSortingModel,
} from '../../models';
import { TaskViewElementEnum } from '@/components/tasks-kanban/enums';
import { TaskFilterModel as TaskKanbanFilterModel } from '@/components/tasks-kanban/models';
import { TasksKanbanFilter } from '@/components/tasks-kanban/components';
import { LabelContent } from '@/components';
import ProjectTaskLi from './project-task-li.vue';
import { TaskHeader } from '../task-header';
import { useRouter } from 'vue-router';

/**Адаптер */
const adapter = new TasksOrderPagesAdapterService();
/**Маршруты */
const router = useRouter();

const updateTaskAdapter = UpdateTaskAdapterService.getInstance();

// #region definProps
const props = defineProps({
  /**id юзера из router */
  routerUserId: { type: Number },
  /**id проекта из router */
  routerProjectId: { type: Number },
  /**Id пользователя */
  userId: { type: Number, required: true },
  /**Информация о проекте */
  project: { type: Object as PropType<ProjectModel>, required: true },
  /**Id задач из очередности выполнения */
  executionQueueIds: { type: Array as PropType<number[]>, default: () => [] },
  /**Включить прокрутку по открытию */
  enableScrollByOpen: { type: Boolean, default: false },
  /**Смещение прокрутки */
  scrollingOffset: { type: Number, default: 20 },
});
// #endregion definProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие добавление задачи в очередность выполнения */
  add: [task: OrderTaskModel];
  /**Событие открытия шторки задачи */
  openTaskDrawer: [taskId: number];
  /**Событие удаления задачи из очередности */
  removeQueue: [task: OrderTaskModel];
  /**Удаление задачи */
  removeTask: [taskId: number];
}>();
// #endregion defineEmits

// #region Переменные
/**Колонки */
const columns: TaskColumnModel[] = [
  new TaskColumnModel({
    Type: TaskViewElementEnum.Namespace,
    Name: '',
    Width: '1.5rem',
    MinWidth: '1.5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Queue,
    Name: 'Очередь',
    Width: '9.375rem',
    MinWidth: '9.375rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Number,
    Name: '№',
    Width: '5rem',
    MinWidth: '5rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Name,
    Name: 'Название',
    Width: '50vw',
    MinWidth: '20rem',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Status,
    Name: 'Статус',
    Width: '5.1875rem',
    MinWidth: '5.1875rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Priority,
    Name: 'Приоритет',
    Width: '4rem',
    MinWidth: '4rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.Difficulty,
    Name: 'Сложность',
    Width: '2rem',
    MinWidth: '2rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.EstimateTime,
    Name: 't (оценка)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
  new TaskColumnModel({
    Type: TaskViewElementEnum.WorkTime,
    Name: 't (затрачено)',
    Width: '5rem',
    MinWidth: '5rem',
    Align: 'center',
  }),
];
/**Дефолтное кол-во элементов на странице */
const DEFAULT_PAGE_STEP = 10;
// #endregion Переменные

// #region refs
/**Кол-во задач */
const countTasks = ref<number>();

/**Сотояние получения данных фильтра */
const stateReceivingFilter = ref(true);
/**Кол-во элементов на странице */
const pageStep = ref(DEFAULT_PAGE_STEP);
/**Страница */
const pageResponse = ref<PaginationResponseModel<Array<OrderTaskModel>>>();
/**Фильтр */
const filter = ref(new TaskFilterModel());
/**Настройки видимости */
const viewSettings = ref([
  TaskViewElementEnum.Namespace,
  TaskViewElementEnum.Queue,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Status,
  TaskViewElementEnum.Priority,
  TaskViewElementEnum.Difficulty,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
]);
/**Данные сортировки */
const sortingData = ref(
  new TaskSortingModel({
    OrderById: true,
  }),
);
/**Загрузка при сортировке */
const isLoadingSorting = ref(false);
/**Открыт ли список */
const visible = ref(false);
/**Контейнер карточки */
const $container = ref<HTMLElement>();
/**Ссылка на элемент скролла */
const $scroll = inject<Ref<HTMLElement | undefined>>('wrapperElement', ref(undefined));

const $tasksKanbanFilter = ref<HTMLElement>();
// #endregion refs

// #region computed
/**Текст заголовка */
const headerText = computed(() => `${props.project.Name}`);
/**Список задач */
const tasks = ref<Array<OrderTaskModel>>([]);

/**Текущая страница */
const currentPageRequest = computed(() => {
  if (pageResponse.value)
    return new PaginationRequestModel({
      Skip: pageResponse.value.Size * (pageResponse.value.Current - 1),
      Take: pageStep.value,
    });
});
/**Кол-во страниц */
const pagesCount = computed(
  () => pageResponse.value && Math.ceil(pageResponse.value.Count / pageResponse.value.Size),
);
/**Идентификаторы задач */
const tasksIds = computed<Array<number>>(() => tasks.value.map((task) => task.Id));
// #endregion computed

// #region watch
watch(
  () => tasksIds.value,
  async (currentValue, prevValue) => {
    if (!currentValue) return;
    // Список заач был изменен
    if (
      currentValue.length === prevValue.length &&
      currentValue.every((value) => prevValue.some((_value) => value === _value))
    )
      return;

    if (currentValue.length) await updateTaskAdapter.subscribeTaskList(currentValue);
  },
  { deep: true },
);
// #endregion watch

// #region Хуки жизненного цикла

onMounted(async () => {
  if (props.routerUserId == props.userId && props.routerProjectId === props.project.Id) {
    await updateVisible(true);
    router.replace({ query: {} });
  }
});

onMounted(() => {
  updateTaskAdapter.OnUpdateTaskEvent.on(onUpdateTask);
  updateTaskAdapter.OnAddOrRemoveEmployeeTask.on(onAddRemovePerformerTask);
});
onUnmounted(async () => {
  updateTaskAdapter.OnUpdateTaskEvent.removeListener(onUpdateTask);
  updateTaskAdapter.OnAddOrRemoveEmployeeTask.removeListener(onAddRemovePerformerTask);
});

// #endregion Хуки жизненного цикла

// #region Функции
/**При добавлении или удалении сотрудника из исполнителей задачи */
const onAddRemovePerformerTask = async (data: AddOrRemoveTaskEventDataModel) => {
  if (data.ProjectId === props.project.Id) {
    await debounceGetTasksCurrentPage();
  }
};
/**При обновлении задачи */
const onUpdateTask = async (taskId: number) => {
  if (!tasksIds.value.includes(taskId)) return;
  await debounceGetTasksCurrentPage();
};
/**Получить задачи текущей страницы */
const getTasksCurrentPage = async () => {
  await getTasks(currentPageRequest.value);
};

const debounceGetTasksCurrentPage = debounce(getTasksCurrentPage, 500);
/**
 * Получить список задач
 * @param page Страница
 */
const getTasks = async (page: PaginationRequestModel, clear?: Boolean) => {
  if (clear) {
    tasks.value = [];
  }
  const response = await adapter.getTasksByProjectId(
    new GetProjectTasksRequestModel({
      ProjectId: props.project.Id,
      Filter: new TaskFilterModel({
        ...filter.value,
        Performers: [props.userId],
        Namespaces: filter.value.Namespaces,
        Queues: filter.value.Queues,
        Statuses: filter.value.Statuses,
        Priorities: filter.value.Priorities,
        Difficulties: filter.value.Difficulties,
        Tags: filter.value.Tags,
        WithoutPriorities: filter.value.WithoutPriorities,
        WithoutTags: filter.value.WithoutTags,
      }),
      Page: page,
      Sort: sortingData.value,
    }),
  );

  if (response.IsSuccess) {
    pageResponse.value = response;
    tasks.value = response?.Items || [];
    countTasks.value = response.Count;
  } else {
    TirPmNotificationEventBus.emit(
      'add',
      new TirPmNotificationModel({
        title: response.ErrorMessage ?? 'Ошибка получения списка задач',
        type: 'error',
        group: 'general',
        life: 5000,
        closable: true,
      }),
    );
  }

  return response.IsSuccess;
};
/**Получить данные */
const getData = async () => {
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};
/**Очистить данные */
const clearData = () => {
  pageResponse.value = undefined;
  tasks.value = [];
  pageStep.value = DEFAULT_PAGE_STEP;
};

/**
 * Фильтрация идентификаторов для сужения типов
 * @param ids Список идентификаторов
 * @param type Тип идентификатора
 */
const filterIds = <T extends 'number' | 'string'>(
  ids: Array<string | number | undefined>,
  type: T,
) => {
  console.log(ids);
  return ids.filter((id) => typeof id === type) as Array<T extends 'number' ? number : string>;
};

/**
 * При обновлении фильтра
 * @param kanbanFilter Фильтр канабан
 */
const onUpdateFilter = (kanbanFilter: TaskKanbanFilterModel) => {
  filter.value = new TaskFilterModel({
    ...filter.value,
    Statuses: filterIds(
      kanbanFilter.Status.map((_status) => _status.StatusId),
      'number',
    ),
    Priorities: filterIds(
      kanbanFilter.Priority.map((_priority) => _priority.PriorityId),
      'number',
    ),
    Difficulties: filterIds(
      kanbanFilter.Difficulty.map((_difficulty) => _difficulty.DifficultyId),
      'string',
    ),
    Tags: filterIds(
      kanbanFilter.Tag.map((_tag) => _tag.Id),
      'number',
    ),
    Namespaces: filterIds(
      kanbanFilter.Namespaces.map((_namespace) => _namespace.Id),
      'string',
    ),
    Queues: filterIds(
      kanbanFilter.Queue.map((_queue) => _queue.Id),
      'string',
    ),
    WithoutPriorities: kanbanFilter.WithoutPriorities,
    WithoutTags: kanbanFilter.WithoutTags,
    TimeEstimate: {
      Start: kanbanFilter.TimeEstimateFrom,
      End: kanbanFilter.TimeEstimateTo,
    },
    TimeSpent: {
      Start: kanbanFilter.WorkTimeFrom,
      End: kanbanFilter.WorkTimeTo,
    },
  });

  getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};

/**Обновить тип сортировки */
const updateSortData = async (data: TaskSortingModel) => {
  if (!currentPageRequest.value) return;
  isLoadingSorting.value = true;
  sortingData.value = new TaskSortingModel(data);
  await getTasks(currentPageRequest.value);
  isLoadingSorting.value = false;
};

const scrollToElementTop = ($scroll: HTMLElement, el: HTMLElement): void => {
  // Получаем верхнюю позицию `el` относительно `scroll`
  const scrollOffset = el.getBoundingClientRect().top - $scroll.getBoundingClientRect().top;

  // Плавно прокручиваем контейнер
  $scroll.scrollTo({
    top: $scroll.scrollTop + scrollOffset - props.scrollingOffset,
    behavior: 'smooth',
  });
};

/**При поиске */
const onSearch = async () => {
  if (!currentPageRequest.value) return;
  await getTasks(currentPageRequest.value, true);
};
/**Обновить текущую страницу */
const changeCurrentPage = async (value: number) => {
  if (!pageResponse.value) return;

  await getTasks(
    new PaginationRequestModel({
      Skip: pageResponse.value.Size * (value - 1),
      Take: pageStep.value,
    }),
  );
};

/**При поиске через debounce */
const debounceOnSearch = debounce(onSearch, 100);
/**Сброс фильтра */
const resetFilter = () => {
  filter.value = new TaskFilterModel();
};
/**
 * Обновить флаг видимости
 * @param value
 */
const updateVisible = async (value: boolean) => {
  if (value) {
    await getData();
    stateReceivingFilter.value = false;
    // $tasksKanbanFilter.value?.getFilter();
  } else {
    clearData();
  }
  visible.value = value;

  if (value && $scroll.value && $container.value && props.enableScrollByOpen) {
    await nextTick();
    setTimeout(() => {
      scrollToElementTop($scroll.value, $container.value);
    }, 100);
  }
};

/**Изменить кол-во элементов на странице */
const changePageStep = async (value: number) => {
  pageStep.value = value;
  await getTasks(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};
/**Проверка нахождения задачи в очередности */
const checkExecution = (taskId: number) => {
  return !!props.executionQueueIds?.includes(taskId);
};
// #endregion Функции
</script>

<template>
  <div class="project-tasks__wrapper" ref="$container">
    <LabelContent
      class="project-tasks"
      :delay="0"
      flag-mode
      :visible="visible"
      @update:visible="updateVisible"
    >
      <template #header>
        <div class="project-tasks__header">
          <TirPmIconView
            is-rounded
            :is-bordered="false"
            :size="24"
            :color-code="project.PreviewColor"
          >
            <template #mainSlot>
              <img :src="project.ImageUrl" v-if="project?.ImageUrl" />
              <span v-else v-text="project?.Name?.[0]" />
            </template>
          </TirPmIconView>
          {{ headerText }}
          <div v-if="countTasks">({{ countTasks }})</div>
        </div>
      </template>
      <template #default>
        <div class="project-tasks__content">
          <div class="project-tasks__filter-header">
            <TasksKanbanFilter
              ref="$tasksKanbanFilter"
              v-model:view-elements="viewSettings"
              tasksOrderPages
              :tasks="tasks"
              :projectId="project.Id"
              :stateReceivingFilter="stateReceivingFilter"
              @on-search="debounceOnSearch"
              @filterOptions="onUpdateFilter"
              @cancelFilter="resetFilter"
            />
          </div>
          <TirPmScrollbar v-if="tasks?.length">
            <div class="project-tasks__main">
              <TaskHeader
                class="project-tasks__columns-header"
                :sort-data="sortingData"
                :is-loading="isLoadingSorting"
                :columns="columns"
                :enable-sorting="true"
                :view-settings="viewSettings"
                @update:sort-data="updateSortData"
              />
              <div class="project-tasks__list">
                <ProjectTaskLi
                  v-for="task in tasks"
                  showStar
                  :key="task.Id"
                  :task="task"
                  :columns="columns"
                  :view-settings="viewSettings"
                  :at-execution="checkExecution(task?.Id)"
                  @add="emit('add', task)"
                  @open-task-drawer="emit('openTaskDrawer', $event)"
                  @removeQueue="emit('removeQueue', task)"
                  @removeTask="emit('removeTask', $event)"
                />
              </div>
            </div>
          </TirPmScrollbar>
          <div v-else class="project-tasks__no-results-block">
            <h2>Тикеты по выбранным в фильтре/поиске критериям не найдены</h2>
          </div>
          <TirPmPagination
            v-if="pageResponse?.Current && pagesCount"
            class="project-tasks__paginator"
            showMoreElements
            :model-value="pageResponse.Current"
            :range-size="3"
            :pages="pagesCount"
            :options="[10, 20, 30, 40, 50, 100]"
            :count-elements="pageStep"
            @update:count-elements="changePageStep"
            @update:model-value="changeCurrentPage"
          />
        </div>
      </template>
    </LabelContent>
  </div>
</template>

<style lang="scss" scoped>
.project-tasks {
  &__no-results-block {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--text-primary);
  }
  &__main {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: 2.5rem;
    margin-bottom: 1rem;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  &__paginator {
    margin-left: 2.5rem;
    margin-top: 0.5rem;
  }
}
</style>
